import { zodResolver } from '@hookform/resolvers/zod'
import { CalendarOff } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { toast } from '@/components/feedback/toast-store'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { useSaveHoliday } from '../api'
import { holidaySchema, type HolidayFormValues } from '../schemas'
import { holidayTypeLabel } from '../status'
import type { Holiday, HolidayType } from '../types'

const FIELDS = ['date', 'name', 'type'] as const
const FORM_ID = 'holiday-form'

interface HolidayDialogProps {
  holiday: Holiday | null
  onClose: () => void
}

/** Create/edit a holiday. Mount only while open so the form starts fresh. */
export function HolidayDialog({ holiday, onClose }: HolidayDialogProps) {
  const { t } = useTranslation('attendance')
  const save = useSaveHoliday()
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<HolidayFormValues>({
    resolver: zodResolver(holidaySchema),
    defaultValues: { date: holiday?.date ?? '', name: holiday?.name ?? '', type: holiday?.type ?? 'school' },
  })

  const submit = handleSubmit(async (values) => {
    try {
      const saved = await save.mutateAsync({ ...values, id: holiday?.id })
      toast.success(holiday ? t('Holiday updated') : t('Holiday added'), saved.name)
      onClose()
    } catch (e) {
      applyServerErrors(e, setError, FIELDS)
    }
  })

  return (
    <Dialog
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      icon={<CalendarOff />}
      title={holiday ? t('Edit {{name}}', { name: holiday.name }) : t('Add a holiday')}
      description={t('No attendance is taken on holidays.')}
      size="sm"
      dismissible={!save.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            {t('Cancel')}
          </Button>
          <Button type="submit" form={FORM_ID} loading={save.isPending}>
            {holiday ? t('Save changes') : t('Add holiday')}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={(e) => void submit(e)} noValidate className="grid gap-4">
        <FormField label={t('Date')} error={errors.date?.message} required>
          <Input type="date" {...register('date')} />
        </FormField>
        <FormField label={t('Name')} error={errors.name?.message} required>
          <Input placeholder={t('Independence Day')} autoComplete="off" {...register('name')} />
        </FormField>
        <FormField label={t('Type')} error={errors.type?.message} required>
          <Select
            options={(Object.keys(holidayTypeLabel) as HolidayType[]).map((type) => ({
              value: type,
              label: t(holidayTypeLabel[type]),
            }))}
            {...register('type')}
          />
        </FormField>
      </form>
    </Dialog>
  )
}
